import { prepareHeaders } from '@/lib/cookie'
import { BASE_URL } from '@/constant'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

export interface User {
  id: number
  name: string
  email: string
  status: 'active' | 'inactive' | string
  team_id: number | null
  team_name: string | null
  is_team_leader: boolean
  invite_token: string | null
  role_id: number
  role_name: string
}

export interface GetUserParams {
  page?: number
  limit?: number
  search?: string
  sort_by?: string | undefined
  order?: 'ASC' | 'DESC' | undefined
  status?: 'active' | 'inactive' | 'all'
}

export interface UpdateUserStatusPayload {
  user_id: number
  status: 'active' | 'inactive'
}

export interface Role {
  id: number
  name: string
  attio: number
  clients: number
  configurations: number
  datascope: number
  projects: number
  timesheet: number
  employees: number
  finance: number
  mini_finance: number
  weekly_planning: number
  admin_notes: number
  vendors: number
  widgets: number
  edit_project_dates: number
  created_at: string
}

export interface RolesResponse {
  success: boolean
  data: Role[]
}

export interface UserResponse {
  success: boolean
  data: User[]
  page: number
  limit: number
  total: number
  totalPages: number
}

export interface InviteUserPayload {
  name: string
  email: string
  role_id: number
  team_id?: number | null
}
export interface UpdateUser {
  user_id: number
  role_id: number
  status: string
  team_id?: number | null
}
interface SelectData {
  id: number
  name: string
}
interface RoleSelect {
  data: SelectData[]
}
export interface UpsertRoles {
  role_id?: number
  name: string
  configurations: boolean
  clients: boolean
  attio: boolean
  employees: boolean
  timesheet: boolean
  projects: boolean
  datascope: boolean
  finance: boolean
  mini_finance: boolean
  weekly_planning: boolean
  admin_notes: boolean
  vendors: boolean
  widgets: boolean
  edit_project_dates: boolean
}
export interface AcceptInvitePayload {
  token: string
  password: string
  type: number
}
export const userManagementApi = createApi({
  reducerPath: 'userManagementApi',
  baseQuery: fetchBaseQuery({
    baseUrl: BASE_URL,
    prepareHeaders: prepareHeaders,
  }),
  tagTypes: ['User', 'Role'],
  endpoints: builder => ({
    getUser: builder.query<UserResponse, GetUserParams>({
      query: ({
        page = 1,
        limit = 10,
        search = '',
        sort_by,
        order,
        status = 'active',
      }) => ({
        url: '/auth/users',
        params: { page, limit, search, sort_by, order, status },
      }),
      providesTags: ['User'],
    }),
    getRoles: builder.query<RolesResponse, { search?: string }>({
      query: ({ search = '' }) => ({
        url: '/auth/roles',
        params: { search },
      }),
      providesTags: ['Role'],
    }),
    getRolesSelect: builder.query<RoleSelect, void>({
      query: () => ({
        url: '/auth/role-select',
      }),
      providesTags: ['Role'],
    }),
    inviteUser: builder.mutation<
      { success: boolean; message: string },
      InviteUserPayload
    >({
      query: payload => ({
        url: '/auth/invite',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['User'],
    }),
    updateUser: builder.mutation<
      { success: boolean; message: string },
      UpdateUser
    >({
      query: payload => ({
        url: '/auth/user',
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: ['User'],
    }),
    updateUserStatus: builder.mutation<
      { success: boolean; message: string },
      UpdateUserStatusPayload
    >({
      query: ({ user_id, status }) => ({
        url: `/auth/user/${user_id}/status`,
        method: 'PATCH',
        body: { status },
      }),
      invalidatesTags: ['User'],
    }),
    upsertRoles: builder.mutation<
      { success: boolean; message: string },
      UpsertRoles
    >({
      query: payload => ({
        url: '/auth/roles',
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: ['Role'],
    }),
    deleteRole: builder.mutation({
      query: (id: number) => ({
        url: `/auth/roles/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Role'],
    }),
    acceptInvite: builder.mutation<
      { success: boolean; message: string },
      AcceptInvitePayload
    >({
      query: payload => ({
        url: '/auth/accept-invite',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['User'],
    }),
  }),
})

export const {
  useGetUserQuery,
  useGetRolesQuery,
  useInviteUserMutation,
  useGetRolesSelectQuery,
  useUpdateUserMutation,
  useUpdateUserStatusMutation,
  useUpsertRolesMutation,
  useDeleteRoleMutation,
  useAcceptInviteMutation,
} = userManagementApi
